import { Subject } from 'rxjs/internal/Subject';
import { Injectable } from '@angular/core';
import { HttpErrorHandler, HandleError } from '@app/shared/services/http-error-handler.service';
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { HeadersSetterHelper } from '@app/shared/helpers/http-headers-setter-helper';
import { Observable, of, observable, BehaviorSubject } from 'rxjs';
import { ISpringResponseBody, observeTypes } from '@app/shared/interfaces/response-interfaces';
import { AppConfig } from '@app/config/app.config';
import { catchError, tap, retryWhen, delay, retry, map } from 'rxjs/operators';
import { ICertificateData, CertStatus } from '@app/certificates/manage/manage';
import {
  ActionCertificatesGet, ActionCertificatesHold,
  ActionCertificatesSuccessChangeState, ActionCertificatesRestoreRequest
} from './manage.reducer';
import { DevicesService } from '../devices/devices.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ManageService {
  private handleError: HandleError;
  private headerHelper: HeadersSetterHelper;

  constructor(
    private httpErrorHandler: HttpErrorHandler,
    private http: HttpClient,
    private store: Store<any>,
    private devicesService: DevicesService,
    private logger: LoggerService,
    private info: InfoService,
    private translateService: TranslateService
  ) {
    this.handleError = this.httpErrorHandler.createHandleError('ManageService');
    this.headerHelper = new HeadersSetterHelper(this.store);
  }

  getAll(): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.certificates, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('getAll')),
    );
  }

  /**
   * Прави POST заявка за добавяне на удостоверение към устройство.
   * При успех се връща Observable<string>, съдържащ QR код, който да се сканира от приложението.
   * @param certReqId
   */
  addDevice(certReqId: string): Observable<string | false> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.operations.addDevice + '/' + certReqId, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('addDevice')),
      map((res: ISpringResponseBody) => {
        if (res.status === 200) {
          return res.data;
        } else {
          return false;
        }
      }),
    );
  }

  /**
   * Проверява статус на заявка за добавяне на удостоверение към устройство
   * @param certReqId
   */
  checkDeviceAdded(qrCode: string | false): Observable<boolean> {
    const errorOneDeviceManyCertificates = -1;
    const resultTexts = 'manageTexts.resultTexts.';
    if (!qrCode) {
      return of(false);
    }
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.operations.checkDevice + '/' + qrCode, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      delay(AppConfig.repeatTimeout),
      catchError((err) => {
        this.handleError('checkDeviceAdded');
        let newErrRes: ISpringResponseBody;
        if (err.error.data === errorOneDeviceManyCertificates) {
          newErrRes = {
            code: 'CUSTOM_ERROR', data: errorOneDeviceManyCertificates,
            message: 'Error:' + errorOneDeviceManyCertificates, status: 1
          };

        } else {
          newErrRes = {
            code: err.error.code, data: err.error.data,
            message: err.error.manage, status: err.error.status
          };
        }
        return of(newErrRes);
      }),
      map((res: ISpringResponseBody) => {
        if (res.data === 1) {
          this.store.dispatch(new ActionCertificatesGet());
          this.devicesService.initAllDevices();
          return true;
        } else if (res.data === 0) {
          throw new Error('not confirmed yet retrying..');
        } else if (res.data === errorOneDeviceManyCertificates) {
          this.info.error(this.translateService.instant(resultTexts + 'deviceErrorBindManyCert'));
          return false;
        } else {
          this.info.error(this.translateService.instant(resultTexts + 'operationFailed'));
          return false;
        }
      }),
      retry({delay: (errors) => errors.pipe(tap(() => console.log('checkDeviceAdded wait ..', errors)))}),
    );
  }

  hold(certReqId: ICertificateData['certReqId']): void {

    this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.operations.hold, certReqId, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('hold'))
    ).subscribe((response: ISpringResponseBody) => {
      if (response.status === 200) {
        this.store.dispatch(new ActionCertificatesHold({ certReqId }));
      }
    });

  }

  restore(certReqId: ICertificateData['certReqId']): void {
    const resultTexts = 'manageTexts.resultTexts.';
    this.http.post<ISpringResponseBody>(
      AppConfig.endpoints.operations.restore, certReqId, this.headerHelper.options(observeTypes.BODY)
    ).pipe(
      catchError(this.handleError('restore'))
    ).subscribe((response: ISpringResponseBody) => {
      if (response.status === 200) {
        this.store.dispatch(new ActionCertificatesRestoreRequest({ certReqId }));
      } else {
        this.info.error(this.translateService.instant(resultTexts + 'cerRestoreFailed'));
      }
    });
  }

  getCert(certReqId: string): Observable<any> {
    return this.http.get(
      AppConfig.endpoints.operations.getCertificate + '/' + certReqId, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        catchError(this.handleError('getCertificate'))
      );
  }

  getCertDetails(certId: string): Observable<ISpringResponseBody | any> {
    return this.http.get<ISpringResponseBody>(
      AppConfig.endpoints.certificates + '/' + certId, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        catchError(this.handleError('getCertificateDetails'))
      );
  }

  getCertificateStatus(certificates: ICertificateData[], pendingCertsInProgress$: Subject<void>): Observable<ISpringResponseBody | any> {
    const pendingCertReqId = certificates.map(certificate => certificate.certReqId).join();
    const headerOptions = this.headerHelper.options(observeTypes.BODY);
    const url = AppConfig.endpoints.operations.certificateStatus.replace('certReqId', pendingCertReqId);

    return this.http.get<ISpringResponseBody>(url, this.headerHelper.options(observeTypes.BODY)).pipe(
     
      delay(AppConfig.pullCertificateStatusTimeout),
      catchError(this.handleError('getCertificateStatus')),
      map((res:ISpringResponseBody) => {
        
        const pendingCerts = res.data.filter(e => e.status === CertStatus.PENDING_HOLD
          || e.status === CertStatus.PENDING_RESTORE);
        const hasPendingCerts = pendingCerts.length > 0;

        const payloads: [any] = res.data.map(element => {
          return { certReqId: element.certReqId, certificateStatus: element.status };
        });

        this.store.dispatch(new ActionCertificatesSuccessChangeState(payloads));
        if (hasPendingCerts && !pendingCertsInProgress$.isStopped) {
          throw 202;
        }

        return null;
      }),
      
      retry({delay: errors => errors.pipe(tap(() => console.log('Certificate status check: Pending certificates exist. Retrying...')))}));
  }

  updateCertAlias(certId: string, alias: string) {
    return this.http.put<ISpringResponseBody>(
      AppConfig.endpoints.certificates + '/' + certId + '/alias', { alias }, this.headerHelper.options(observeTypes.BODY))
      .pipe(
        catchError(this.handleError('saveCertificateAlias'))
      );
  }

}
